import { IconButton, SxProps, Theme, CircularProgress, Tooltip, Box } from '@mui/material';
import { HeartFillIcon, HeartIcon } from '../../assets/icons';
import { useGetSignedInUser } from '../../hooks/useGetSignedInUser';
import { WishlistModuleType } from '../../store/features/wishlist/types';
import { StrOrNum } from '../../types/util';
import { AppIcon } from '../app-icon/AppIcon';
import { wishlistButtonStyles } from './styles';
import { useUpdateWishlistsMutation } from '../../store/features/wishlist/wishlistSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { APP_URLS } from '../../routes/urls';
import LoadingButton from '@mui/lab/LoadingButton';

export interface WishlistButtonProps {
  type: WishlistModuleType;
  moduleId: StrOrNum;
  sx?: SxProps<Theme>;
  btnType?: 'icon' | 'label';
  isDisabled?: boolean;
}
export const WishlistButton: React.FC<WishlistButtonProps> = ({ sx, moduleId, type, btnType = 'icon', isDisabled }) => {
  const { currentData: userData, isFetching } = useGetSignedInUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [updateWishlist, { isLoading }] = useUpdateWishlistsMutation({
    fixedCacheKey: `action:wishlist|type:${type}|id:${moduleId}`,
  });

  // Not showing wishlist button in case scholarship or program is already applied
  const getIsApplied = (): boolean => {
    if (userData?.user) {
      switch (type) {
        case 'institute_programs':
          return (
            userData.user.applies.programs?.findIndex(
              (program) => program.applicable_id.toString() === moduleId.toString()
            ) !== -1
          );
        case 'scholarships':
          return (
            userData.user.applies.scholarships?.findIndex(
              (scholarship) => scholarship.applicable_id.toString() === moduleId.toString()
            ) !== -1
          );
        default:
          return false;
      }
    }
    return false;
  };
  const isApplied = getIsApplied();
  if (isApplied) return null;

  const getIsAddedToWishlist = (): boolean => {
    if (userData?.user) {
      switch (type) {
        case 'institute_programs':
          return userData.user.wishlists.programs.findIndex((program) => program.id === moduleId) !== -1;
        case 'institutes':
          return userData.user.wishlists.institutes.findIndex((institute) => institute.id === moduleId) !== -1;
        case 'scholarships':
          return userData.user.wishlists.scholarships.findIndex((scholarship) => scholarship.id === moduleId) !== -1;
      }
    }
    return false;
  };

  const isAddedToWishlist = getIsAddedToWishlist();
  const isWishlistLoading = isLoading || isFetching;

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!isWishlistLoading) {
      if (userData?.user) {
        updateWishlist({
          method: isAddedToWishlist ? 'REMOVE' : 'ADD',
          apply: [moduleId],
          slug: userData.user.slug,
          type,
          user_id: userData.user.user_id,
        });
      } else {
        navigate(APP_URLS.signIn, {
          state: {
            from: location,
          },
        });
      }
    }
  };

  const getTooltipTitle = (): string => {
    if (userData?.user) {
      if (isAddedToWishlist) {
        return 'Remove from Wishlist';
      } else {
        return 'Add to Wishlist';
      }
    } else {
      return 'Sign in to Add to Wishlist';
    }
  };

  return btnType === 'icon' ? (
    <Tooltip placement="top" title={getTooltipTitle()}>
      <Box className="WishlistBtn-root" sx={{ display: 'inline-block', ...sx }} component="span">
        <IconButton
          disabled={isDisabled}
          component="button"
          onClick={handleClick}
          sx={wishlistButtonStyles.btn}
          aria-label="Add to Wishlist"
          color="primary"
        >
          {isWishlistLoading && <CircularProgress sx={{ position: 'absolute' }} size="1rem" color="secondary" />}
          <AppIcon
            sx={isWishlistLoading ? { opacity: 0 } : undefined}
            Icon={isAddedToWishlist ? HeartFillIcon : HeartIcon}
          />
        </IconButton>
      </Box>
    </Tooltip>
  ) : (
    <LoadingButton
      color="primary"
      disabled={isDisabled}
      onClick={handleClick}
      sx={{ ...wishlistButtonStyles.btn, ...sx }}
      loading={isWishlistLoading}
      endIcon={<AppIcon Icon={isAddedToWishlist ? HeartFillIcon : HeartIcon} />}
    >
      {getTooltipTitle()}
    </LoadingButton>
  );
};
